import { Middleware, configureStore } from '@reduxjs/toolkit';
import { loadStateFromCookie, loadStateFromSessionStorage, saveStateToSessionStorage } from 'utilities';
import rootReducer from './reducers';

let hasRegisteredReloadListener = false;

// Before the page unloads (e.g., reload or close), set a flag in sessionStorage
// to indicate that the next load is a reload event.
const persistReloadFlag = (): void => {
	try {
		sessionStorage.setItem('reloaded', 'true');
	} catch {
		// Ignore storage failures on restricted browsers.
	}
};

const registerReloadListener = (): void => {
	if (hasRegisteredReloadListener) {
		return;
	}

	hasRegisteredReloadListener = true;

	window.addEventListener('pagehide', persistReloadFlag);
	window.addEventListener('beforeunload', persistReloadFlag);
};

// Load the initial state from session storage and cookies
const preloadedState = {
	...loadStateFromSessionStorage(),
	...loadStateFromCookie(),
};

registerReloadListener();

const persistenceMiddleware: Middleware = (store) => (next) => (action) => {
	const result = next(action);

	const state = store.getState() as RootState;

	saveStateToSessionStorage(state);

	return result;
};

export const store = configureStore({
	reducer: rootReducer,
	preloadedState,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({ serializableCheck: false }).concat(persistenceMiddleware),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
